import { BRICK_SPECS } from '@bw/core';
import type { BrickKind } from '@bw/core';
import type { EditorController } from './editor/controller.js';
import { useEditor } from './state/store.js';
import { TOOL_META } from './state/tools.js';
import type { ToolId } from './state/tools.js';

export interface Command {
  readonly id: string;
  readonly label: string;
  readonly group: string;
  /** Display form, e.g. "Ctrl+S". Matched case-insensitively by the keymap. */
  readonly shortcut?: string;
  readonly enabled?: () => boolean;
  readonly run: () => void | Promise<void>;
}

export interface CommandHooks {
  readonly controller: EditorController;
  readonly newProject: () => void | Promise<void>;
  readonly openProject: () => void | Promise<void>;
  readonly openExample: () => void | Promise<void>;
  readonly saveProject: (saveAs: boolean) => void | Promise<void>;
  readonly showLibrary: () => void;
  readonly showHelp: () => void;
  readonly showExport: () => void;
  readonly showOutline: () => void;
  readonly showPalette: () => void;
  readonly addWall: () => void;
  readonly importWall: () => void;
  readonly deleteWall: (id: string) => void;
  readonly cycleWall: (delta: number) => void;
  readonly confirmClear: () => void;
}

/**
 * Every user action is a Command (design document C6). Menus, the palette, the
 * tool rail and the keyboard layer all read this one registry, so an action
 * can never be enabled in one surface and broken in another.
 */
export function buildCommands(hooks: CommandHooks): Command[] {
  const { controller } = hooks;
  const state = () => useEditor.getState();

  const tool = (id: ToolId, label: string, shortcut: string): Command => ({
    id: `tool.${id}`,
    label,
    group: 'Tools',
    shortcut,
    run: () => {
      state().setTool(id);
      controller.clearMeasurement();
      controller.refreshPreview();
      controller.restStatus();
    },
  });

  const commands: Command[] = [
    { id: 'file.new', label: 'New project', group: 'File', shortcut: 'Ctrl+N', run: hooks.newProject },
    { id: 'file.open', label: 'Open project…', group: 'File', shortcut: 'Ctrl+O', run: hooks.openProject },
    { id: 'file.example', label: `Open the example house`, group: 'File', run: hooks.openExample },
    { id: 'file.save', label: 'Save', group: 'File', shortcut: 'Ctrl+S', run: () => hooks.saveProject(false) },
    { id: 'file.saveAs', label: 'Save as…', group: 'File', shortcut: 'Ctrl+Shift+S', run: () => hooks.saveProject(true) },
    { id: 'file.library', label: 'Recent projects…', group: 'File', run: hooks.showLibrary },

    { id: 'edit.undo', label: 'Undo', group: 'Edit', shortcut: 'Ctrl+Z', enabled: () => state().history.canUndo, run: () => controller.undo() },
    { id: 'edit.redo', label: 'Redo', group: 'Edit', shortcut: 'Ctrl+Y', enabled: () => state().history.canRedo, run: () => controller.redo() },
    { id: 'edit.redoAlt', label: 'Redo', group: 'Edit', shortcut: 'Ctrl+Shift+Z', enabled: () => state().history.canRedo, run: () => controller.redo() },
    { id: 'edit.selectAll', label: 'Select all', group: 'Edit', shortcut: 'Ctrl+A', run: () => controller.selectAll() },
    { id: 'edit.copy', label: 'Copy pattern', group: 'Edit', shortcut: 'Ctrl+C', enabled: () => state().selected.size > 0, run: () => controller.copySelection() },
    { id: 'edit.paste', label: 'Paste pattern', group: 'Edit', shortcut: 'Ctrl+V', enabled: () => state().clipboard !== null, run: () => controller.beginPaste() },
    {
      id: 'edit.duplicate',
      label: 'Duplicate selection',
      group: 'Edit',
      shortcut: 'Ctrl+D',
      enabled: () => state().selected.size > 0,
      run: () => {
        if (controller.copySelection()) controller.beginPaste();
      },
    },
    { id: 'edit.delete', label: 'Delete selection', group: 'Edit', shortcut: 'Delete', enabled: () => state().selected.size > 0 || controller.hasSketchSelection, run: () => controller.deleteSelection() },
    { id: 'edit.deleteBack', label: 'Delete selection', group: 'Edit', shortcut: 'Backspace', enabled: () => state().selected.size > 0 || controller.hasSketchSelection, run: () => controller.deleteSelection() },
    { id: 'edit.clear', label: 'Clear wall…', group: 'Edit', run: hooks.confirmClear },

    tool('select', 'Select', 'V'),
    tool('build', 'Build', 'B'),
    tool('paint', 'Paint', 'X'),
    tool('measure', 'Measure', 'M'),
    tool('pan', 'Pan', 'P'),
    tool('sketch', 'Sketch outline', 'K'),
    tool('plan', 'Plan the building', 'L'),
    {
      id: 'sketch.rectangle',
      label: 'Sketch a rectangle',
      group: 'Tools',
      shortcut: 'G',
      run: () => {
        state().setTool('sketch');
        controller.setDrawMode('rectangle');
        controller.restStatus();
      },
    },
    {
      id: 'sketch.close',
      label: 'Close the sketched shape',
      group: 'Tools',
      shortcut: 'Enter',
      enabled: () => state().tool === 'sketch',
      run: () => controller.closeSketchShape(),
    },
    {
      id: 'sketch.undoPoint',
      label: 'Undo the last sketch point',
      group: 'Tools',
      shortcut: 'Ctrl+Backspace',
      enabled: () => state().tool === 'sketch' && controller.draftPointCount > 0,
      run: () => controller.undoSketchPoint(),
    },
    {
      id: 'tool.rotate',
      label: 'Rotate 90°',
      group: 'Tools',
      shortcut: 'R',
      run: () => {
        // In the plan there is no brick preview to turn, so R turns the wall
        // that is selected — the same key for the same idea in both views.
        if (state().tool === 'plan' && controller.nudgePlan(0, 0, 90)) return;
        state().rotatePreview();
        controller.refreshPreview();
        controller.restStatus();
      },
    },
    {
      id: 'tool.snap',
      label: 'Toggle smart snap',
      group: 'Tools',
      shortcut: 'S',
      run: () => {
        const next = !state().snapEnabled;
        state().setSnapEnabled(next);
        controller.refreshPreview();
      },
    },
    { id: 'edit.escape', label: 'Cancel', group: 'Edit', shortcut: 'Escape', run: () => controller.cancelInteraction() },

    ...(
      [
        ['left', 'ArrowLeft'],
        ['right', 'ArrowRight'],
        ['up', 'ArrowUp'],
        ['down', 'ArrowDown'],
      ] as const
    ).flatMap(([direction, key]) =>
      (
        [
          ['', {}, '1 mm'],
          ['Shift+', { shift: true }, '10 mm'],
          ['Alt+', { alt: true }, '0.125 mm'],
          ['Ctrl+', { ctrl: true }, direction === 'left' || direction === 'right' ? 'half bond' : 'one course'],
        ] as const
      ).map(([prefix, modifiers, label]) => ({
        id: `edit.nudge.${direction}${prefix}`,
        label: `Nudge ${direction} by ${label}`,
        group: 'Edit',
        shortcut: `${prefix}${key}`,
        // In the plan the arrows always have something to move: there is one
        // active wall at all times. In the elevation they need a selection.
        enabled: () => (state().tool === 'plan' ? true : state().selected.size > 0),
        run: () => {
          if (state().tool === 'plan') {
            // A plan step is a distance on the ground, not a bond fraction:
            // 1 mm, 10 with Shift, a quantum with Alt, 100 with Ctrl.
            const held: { shift?: boolean; alt?: boolean; ctrl?: boolean } = modifiers;
            const step = held.ctrl ? 100 : held.shift ? 10 : held.alt ? 0.125 : 1;
            const dx = direction === 'left' ? -step : direction === 'right' ? step : 0;
            // Screen up is world -z, because the plan looks down the -Y axis.
            const dz = direction === 'up' ? -step : direction === 'down' ? step : 0;
            controller.nudgePlan(dx, dz, 0);
            return;
          }
          controller.nudge(direction, modifiers);
        },
      })),
    ),

    { id: 'view.zoomIn', label: 'Zoom in', group: 'View', shortcut: '+', run: () => controller.zoomIn() },
    { id: 'view.zoomOut', label: 'Zoom out', group: 'View', shortcut: '-', run: () => controller.zoomOut() },
    {
      id: 'view.fit',
      label: 'Fit the drawing',
      group: 'View',
      shortcut: 'F',
      // Fit means "frame what I am looking at", and in the plan that is the
      // building rather than the one wall whose elevation is open.
      run: () => (state().tool === 'plan' ? controller.fitPlan() : controller.fit()),
    },
    { id: 'view.panels', label: 'Collapse panels', group: 'View', shortcut: 'Ctrl+\\', run: () => {
      const store = state();
      const open = store.leftPanelOpen || store.rightPanelOpen;
      if (open === store.leftPanelOpen) store.togglePanel('left');
      if (open === store.rightPanelOpen) store.togglePanel('right');
    } },
    {
      id: 'view.3d',
      label: 'View the building in 3D',
      group: 'View',
      shortcut: 'D',
      // Only worth opening once there is something to look at.
      enabled: () => state().project.brickCount > 0,
      run: () => state().setView3D(true),
    },
    { id: 'view.prevWall', label: 'Previous wall', group: 'Walls', shortcut: '[', run: () => hooks.cycleWall(-1) },
    { id: 'view.nextWall', label: 'Next wall', group: 'Walls', shortcut: ']', run: () => hooks.cycleWall(1) },
    { id: 'wall.add', label: 'Add a wall', group: 'Walls', run: hooks.addWall },
    {
      id: 'wall.import',
      label: 'Import a wall…',
      group: 'Walls',
      run: hooks.importWall,
    },
    {
      id: 'wall.delete',
      label: 'Delete this wall…',
      group: 'Walls',
      enabled: () => state().project.walls.length > 1,
      run: () => hooks.deleteWall(state().project.activeWallId),
    },

    { id: 'file.exportPdf', label: 'Export drawing sheets…', group: 'File', shortcut: 'Ctrl+P', run: hooks.showExport },
    { id: 'file.exportPng', label: 'Export image…', group: 'File', shortcut: 'Ctrl+Shift+E', run: hooks.showExport },

    { id: 'help.commands', label: 'Command palette', group: 'Help', shortcut: 'Ctrl+K', run: hooks.showPalette },
    { id: 'help.controls', label: 'Controls', group: 'Help', shortcut: 'F1', run: hooks.showHelp },
    {
      id: 'view.outline',
      label: 'Wall outline (accessible list)',
      group: 'View',
      shortcut: 'Ctrl+Shift+O',
      run: hooks.showOutline,
    },
  ];

  // Number keys pick a unit, in catalogue order (clause 10.3).
  for (const spec of Object.values(BRICK_SPECS)) {
    commands.push({
      id: `tool.brick.${spec.key}`,
      label: `Build with ${spec.name}`,
      group: 'Tools',
      shortcut: spec.shortcut,
      run: () => {
        state().setSelectedKind(spec.key as BrickKind);
        controller.clearMeasurement();
        controller.refreshPreview();
        controller.restStatus();
      },
    });
  }

  return commands;
}

/** Canonical key description for an event, e.g. "Ctrl+Shift+S". */
export function describeKey(event: KeyboardEvent): string {
  const parts: string[] = [];
  if (event.ctrlKey || event.metaKey) parts.push('Ctrl');
  if (event.shiftKey) parts.push('Shift');
  if (event.altKey) parts.push('Alt');
  let key = event.key;
  if (key === ' ') key = 'Space';
  else if (key.length === 1) key = key.toUpperCase();
  parts.push(key);
  return parts.join('+');
}

/**
 * Clause 10.3's guard, which is not optional: bare-letter and bare-digit
 * shortcuts must not fire while a text field has focus, or typing `5` into the
 * joint field switches the brick tool and swallows the keystroke.
 */
export function targetsTextInput(target: EventTarget | null): boolean {
  if (target === null || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable ||
    target.closest('[data-text-input]') !== null
  );
}

export function toolLabel(id: ToolId): string {
  return TOOL_META[id].label;
}
